// Your own pins (fishing spots, stands, boat launches, trail cameras...) and imported routes on the big map.
import type { Pin, PinType, Route } from "../../shared/model.ts";
import { dbAll, dbDel, dbPut } from "../db.ts";
import { $, $maybe, el, icon, input, toast, twoTap } from "../dom.ts";
import { fmtNum, pinTypeLabel, t } from "../i18n.ts";
import { onRender } from "../state.ts";
import { lsGet, lsSet, uid } from "../util.ts";
import { bigMap, setMapFull } from "./bigmap.ts";
import { onMapReady } from "./ready.ts";
import { exportGpx } from "./gpx.ts";
import { sharePin } from "../groups/share.ts";

/** Pin type → icon name. */
export const PIN_TYPES: Record<PinType, string> = { spot: "fish", stand: "stand", boat: "boat", camera: "camera", parking: "parking", other: "fit" };
export const isPinType = (v: string): v is PinType => v in PIN_TYPES;

export let pins: Pin[] = [];
export let routes: Route[] = [];
let pinLayer: L.LayerGroup | null = null, routeLayer: L.LayerGroup | null = null;

export async function loadPlaces(): Promise<void> {
  try { pins = await dbAll("pins"); routes = await dbAll("routes"); }
  catch (e) { console.warn("Could not read pins and routes", e); }
}
/** Adds a pin (already saved to the database) to the map. */
export function addPin(p: Pin): void {
  pins.push(p);
  drawPlaces();
  placesInfo();
}
export const directionsUrl = (lat: number, lon: number): string => `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`;

function userPinIcon(p: Pin): L.DivIcon {
  const d = el("div", { class: "upin" + (p.type === "stand" ? " stand" : "") }, el("span", {}, icon(PIN_TYPES[p.type] ?? "fit")));
  return L.divIcon({ html: d, className: "", iconSize: [36, 36], iconAnchor: [18, 36], popupAnchor: [0, -34] });
}
const routeKm = (pts: Route["points"]) => { let d = 0; for (let i = 1; i < pts.length; i++) d += L.latLng(pts[i - 1]).distanceTo(pts[i]); return d / 1000; };

export function drawPlaces(): void {
  const map = bigMap;
  if (!map) return;
  if (!pinLayer || !routeLayer) { routeLayer = L.layerGroup().addTo(map); pinLayer = L.layerGroup().addTo(map); }
  pinLayer.clearLayers();
  routeLayer.clearLayers();
  for (const r of routes) L.polyline(r.points, { className: "route-line", weight: 4, opacity: 0.9 }).addTo(routeLayer).bindPopup(() => routePopup(r));
  for (const p of pins) {
    L.marker([p.lat, p.lon], { icon: userPinIcon(p), title: p.name || pinTypeLabel(p.type) }).addTo(pinLayer).bindPopup(() =>
      el("div", { class: "pop pinpop" }, el("div", {},
        el("b", { text: p.name || pinTypeLabel(p.type) }),
        el("small", { text: pinTypeLabel(p.type) }),
        p.notes ? el("p", { class: "popnotes", text: p.notes }) : null,
        el("div", { class: "popbtns" },
          el("button", { class: "btn small", type: "button", onclick: () => { map.closePopup(); openPinEditor(p); } }, t("edit")),
          el("a", { class: "btn small", href: directionsUrl(p.lat, p.lon), target: "_blank", rel: "noopener" }, t("directionsShort")),
          el("button", { class: "btn small", type: "button", onclick: () => { map.closePopup(); sharePin(p); } }, t("share"))))));
  }
}
function routePopup(r: Route): HTMLElement {
  const del = twoTap(el("button", { class: "btn small danger", type: "button" }, t("del")), async () => {
    try { await dbDel("routes", r.id); routes = routes.filter((x) => x.id !== r.id); bigMap?.closePopup(); drawPlaces(); placesInfo(); toast(t("routeDeleted")); }
    catch { toast(t("deleteFailed")); }
  }, () => t("del"), () => t("delConfirm"));
  return el("div", { class: "pop" }, el("div", {},
    el("b", { text: r.name || t("route") }),
    el("small", { class: "num", text: t("routeLen", fmtNum(routeKm(r.points), 1)) }),
    el("div", { class: "popbtns" },
      el("button", { class: "btn small", type: "button", onclick: () => void exportGpx({ pins: [], routes: [r], catches: [], name: r.name || "route" }) }, t("gpxSave")), del)));
}
export function placesInfo(): void {
  const p = $maybe("#places-info");
  if (p) p.textContent = t("placesInfo", pins.length, routes.length);
}

/* ---------- placing a new pin at the crosshair ---------- */
function startPlacing(): void { if (!bigMap) return; setMapFull(false); $("#v-map").classList.add("placing"); }
function stopPlacing(): void { $("#v-map").classList.remove("placing"); }
$("#map-pin").addEventListener("click", startPlacing);
$("#place-cancel").addEventListener("click", stopPlacing);
$("#place-ok").addEventListener("click", () => {
  if (!bigMap) return;
  const c = bigMap.getCenter();
  stopPlacing();
  openPinEditor(null, { lat: c.lat, lon: c.lng });
});

/* ---------- pin editor ---------- */
const dlg = $<HTMLDialogElement>("#pin");
let draft: Pin | null = null, delArmed = false;
function openPinEditor(p: Pin | null, at?: { lat: number; lon: number }): void {
  const lastType = lsGet("pintype", "spot");
  draft = p ? { ...p } : { id: uid(), type: isPinType(lastType) ? lastType : "spot", name: "", notes: "", lat: at!.lat, lon: at!.lon, createdAt: Date.now() };
  $("#pin-title").textContent = p ? t("pinEdit") : t("pinNew");
  input("#p-name").value = draft.name || "";
  $<HTMLTextAreaElement>("#p-notes").value = draft.notes || "";
  $("#p-coords").textContent = `${draft.lat.toFixed(5)}, ${draft.lon.toFixed(5)}`;
  $("#p-delete").hidden = !p;
  disarmDelete();
  drawPinTypes();
  dlg.showModal();
}
function drawPinTypes(): void {
  const d = draft;
  if (!d) return;
  $("#p-types").replaceChildren(...(Object.keys(PIN_TYPES) as PinType[]).map((ty) => el("button", { class: "chip ptype", type: "button", "aria-pressed": String(d.type === ty),
    onclick: () => { d.type = ty; drawPinTypes(); } }, icon(PIN_TYPES[ty]), pinTypeLabel(ty))));
}
function disarmDelete(): void { delArmed = false; const b = $("#p-delete"); b.classList.remove("armed"); b.textContent = t("del"); }
$("#pin-form").addEventListener("submit", async (e) => {
  e.preventDefault();
  const d = draft;
  if (!d) return;
  Object.assign(d, { name: input("#p-name").value.trim(), notes: $<HTMLTextAreaElement>("#p-notes").value.trim(), updatedAt: Date.now() });
  try {
    await dbPut("pins", d);
    const i = pins.findIndex((x) => x.id === d.id);
    if (i >= 0) pins[i] = d; else pins.push(d);
    lsSet("pintype", d.type);
    dlg.close(); drawPlaces(); placesInfo(); toast(t("pinSaved"));
  } catch { toast(t("saveFailed")); }
});
$("#p-delete").addEventListener("click", async () => {
  const b = $("#p-delete"), d = draft;
  if (!d) return;
  if (!delArmed) { delArmed = true; b.classList.add("armed"); b.textContent = t("delConfirm"); return; }
  try { await dbDel("pins", d.id); pins = pins.filter((x) => x.id !== d.id); dlg.close(); drawPlaces(); placesInfo(); toast(t("pinDeleted")); }
  catch { toast(t("deleteFailed")); }
});
$("#pin-cancel").addEventListener("click", () => dlg.close());
$("#pin-close").addEventListener("click", () => dlg.close());

onMapReady(drawPlaces);
onRender(() => { drawPlaces(); placesInfo(); });
